import React, { useContext, useEffect, useState } from "react";
import UserReportModal from "../common/Modals/UserReportModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowUp,
  faGear,
  faBell,
  faMessage,
  faUserShield,
  faFlag,
  faArrowRightFromBracket,
  faXmark,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { revokeCurrentSession } from "../../api/sessions";
import { useNavigate, useLocation } from "react-router-dom";
import { getUserById } from "../../api/users";
import { useDispatch, useSelector } from "react-redux";
import { NotificationContext } from "../../context/NotificationContext";
import { useTranslation } from "react-i18next";
import http from "../../http-common";
import { useEpkWizard } from "../../context/EpkWizardContext";
import { useSocket } from "../../context/SocketProvider";

export const SideProfileMenu = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const socket = useSocket();
  const { openWizard } = useEpkWizard();
  const [showReportModal, setShowReportModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const user = useSelector((state) => state.user);
  const [picture, setPicture] = useState("");

  const {
    notificationCount,
    messageCount,
    userInfo,
    clearNotificationCount,
    clearMessageCount,
  } = useContext(NotificationContext) || {};

  const userId = user?.id;

  const menuList = [
    {
      name: t("My Profile"),
      url: `/user/${userId}`,
      icon: faUser,
      display: true,
    },
    {
      name: t("Upload EPK"),
      url: "wizard",
      icon: faCloudArrowUp,
      display: true,
    },
    {
      name: t("My Settings"),
      url: "/dashboard/settings",
      icon: faGear,
      display: true,
    },
    {
      name: t("Notifications"),
      url: "/dashboard/notifications",
      icon: faBell,
      display: true,
      badge: notificationCount > 0 && userInfo === userId ? notificationCount : null,
    },
    {
      name: t("Messages"),
      url: "/dashboard/chat",
      icon: faMessage,
      display: true,
      badge: messageCount > 0 && userInfo === userId ? messageCount : null,
    },
    {
      name: t("Admin"),
      url: "/admindashboard/main",
      icon: faUserShield,
      display: user?.role === "Admin",
    },
    {
      name: t("Report / Feedback"),
      url: "report",
      icon: faFlag,
      display: true,
    },
  ];

  const filteredMenuList = menuList.filter((menu) => menu.display);

  useEffect(() => {
    if (user && user.id) {
      getUserById(user.id)
        .then((res) => {
          setPicture(
            res.picture.startsWith("https")
              ? res.picture
              : `${process.env.REACT_APP_AWS_URL}/${res.picture}`
          );
        })
        .catch((error) => console.error("Error fetching user:", error));
    }
  }, [user, location.pathname]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Session not restored yet (cold load) or logged out: nothing to show.
  if (!user) return null;

  const logout = () => {
    if (user?.id && socket) {
      socket.emit("logout", user.id);
      setTimeout(() => socket.disconnect(), 1000);
    }

    // Fire-and-forget: invalidate session and update last active on server.
    // Goes through the shared http instance so an already-expired access
    // token still gets a silent refresh-and-retry instead of just 401ing
    // and leaving the session (and its refresh cookie) alive server-side.
    if (user?.token) {
      revokeCurrentSession().catch(() => {});
    }
    if (user?.id) {
      http.put(`/users/lastactive/${user.id}`).catch(() => {});
    }

    dispatch({ type: "LOGOUT", payload: null });
    onClose();
    navigate("/");
  };

  const handleItemClick = (menu) => {
    if (menu.name === t("Notifications")) clearNotificationCount();
    else if (menu.name === t("Messages")) clearMessageCount();

    onClose();

    if (menu.url === "report") setShowReportModal(true);
    else if (menu.url === "wizard") openWizard();
    else navigate(menu.url);
  };

  const panelHeader = (
    <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-border-[#712CB0]/30 tw-px-5 tw-py-4 tw-flex-shrink-0">
      <img
        src={picture}
        alt={t("User Avatar")}
        className="tw-h-12 tw-w-12 tw-flex-shrink-0 tw-rounded-full tw-object-cover tw-ring-2 tw-ring-[#712CB0]"
      />
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-truncate tw-text-base tw-font-semibold tw-text-white">
          {user.firstName} {user.lastName}
        </p>
        <p className="tw-text-sm tw-text-[#712CB0]">{t(user.role)}</p>
      </div>
      {isMobile && (
        <button
          onClick={onClose}
          className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#712CB0]/20 tw-text-gray-400 tw-border-none tw-cursor-pointer hover:tw-text-white tw-transition-colors"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  );

  const panelMenu = (
    <div className="tw-px-2 tw-py-2">
      {filteredMenuList.map((menu, index) => (
        <button
          key={index}
          onClick={() => handleItemClick(menu)}
          className="tw-relative tw-flex tw-w-full tw-items-center tw-gap-4 tw-rounded-xl tw-px-4 tw-py-3 tw-text-left tw-text-base tw-text-gray-300 tw-transition-colors tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-[#712CB0]/20 hover:tw-text-white tw-mb-0.5"
        >
          <FontAwesomeIcon
            icon={menu.icon}
            className="tw-w-5 tw-text-[#712CB0] tw-flex-shrink-0"
          />
          <span className="tw-flex-1">{menu.name}</span>
          {menu.badge && (
            <span className="tw-flex tw-h-6 tw-min-w-[24px] tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500 tw-px-1.5 tw-text-xs tw-font-bold tw-text-white">
              {menu.badge > 9 ? "9+" : menu.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const panelLogout = (pinToBottom = false) => (
    <div className={`tw-border-t tw-border-[#712CB0]/30 tw-px-2 tw-pt-2 tw-flex-shrink-0 ${pinToBottom ? "tw-pb-20" : "tw-pb-2"}`}>
      <button
        onClick={logout}
        className="tw-flex tw-w-full tw-items-center tw-gap-4 tw-rounded-xl tw-px-4 tw-py-3 tw-text-left tw-text-base tw-text-gray-300 tw-transition-colors tw-bg-transparent tw-border-none tw-cursor-pointer hover:tw-bg-red-500/20 hover:tw-text-red-400"
      >
        <FontAwesomeIcon
          icon={faArrowRightFromBracket}
          className="tw-w-5 tw-text-red-400 tw-flex-shrink-0"
        />
        <span>{t("Logout")}</span>
      </button>
    </div>
  );

  const panelContent = (
    <>
      {panelHeader}
      {panelMenu}
      {panelLogout(false)}
    </>
  );

  return (
    <>
      {showReportModal && (
        <UserReportModal onClose={() => setShowReportModal(false)} />
      )}

      {/* Backdrop */}
      <div
        className={`tw-fixed tw-inset-0 tw-z-40 tw-bg-black/60 tw-transition-opacity tw-duration-300 ${
          isOpen ? "tw-opacity-100 tw-pointer-events-auto" : "tw-opacity-0 tw-pointer-events-none"
        }`}
        onClick={onClose}
      />

      {isMobile ? (
        /* Mobile: bottom sheet slide up */
        <div
          className={`tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-50 tw-rounded-t-2xl tw-border-t tw-border-[#712CB0]/30 tw-bg-[#1C0039] tw-shadow-2xl tw-shadow-black/60 tw-transition-transform tw-duration-300 tw-ease-out tw-max-h-[85vh] tw-flex tw-flex-col ${
            isOpen ? "tw-translate-y-0" : "tw-translate-y-full"
          }`}
        >
          {/* Drag handle */}
          <div className="tw-flex tw-justify-center tw-pt-3 tw-pb-1 tw-flex-shrink-0">
            <div className="tw-h-1 tw-w-10 tw-rounded-full tw-bg-[#712CB0]/50" />
          </div>
          {/* Header always visible */}
          {panelHeader}
          {/* Menu items scroll if needed */}
          <div className="tw-overflow-y-auto tw-flex-1 tw-min-h-0">
            {panelMenu}
          </div>
          {/* Logout always pinned at bottom */}
          {panelLogout(true)}
        </div>
      ) : (
        /* Desktop: sidebar drawer from right edge */
        <div
          className={`tw-fixed tw-right-0 tw-top-0 tw-h-screen tw-z-50 tw-w-80 tw-rounded-l-2xl tw-border-l tw-border-[#712CB0]/30 tw-bg-[#1C0039] tw-shadow-[-8px_0_40px_rgba(0,0,0,0.6)] tw-transition-transform tw-duration-300 tw-ease-out ${
            isOpen ? "tw-translate-x-0" : "tw-translate-x-full"
          }`}
        >
          <div className="tw-flex tw-flex-col tw-h-full tw-overflow-y-auto">
            {panelContent}
          </div>
        </div>
      )}
    </>
  );
};
